import React from 'react';
import { Col, Empty, Row, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TransportRouteSummaryDto } from '../../../dto/transport';
import { getTransportLabel } from '../../../utils/transportLabel';

interface Props {
  transports: TransportRouteSummaryDto[];
}

const RATE_FORMATTER = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 2 });

const joinLocation = (addresses: string[], city: string, country: string) => {
  if (addresses.length > 0) return addresses.join(' • ');
  return [city, country].filter(Boolean).join(', ') || '—';
};

const formatRate = (transport: TransportRouteSummaryDto) => {
  const value = transport.transportRate ?? transport.transportCost;
  if (!Number.isFinite(value)) return '—';
  return `${RATE_FORMATTER.format(value)} ${transport.currency || 'PLN'}`;
};

const WZTransportSummary: React.FC<Props> = ({ transports }) => {
  const { t } = useTranslation('wz');
  const active = transports.filter((transport) => transport.status !== 'CANCELLED');

  if (active.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('transport.noLinkedTransport')} />;
  }

  return (
    <div className="wz-transport-summary">
      {active.map((transport) => (
        <div className="wz-transport-summary__route" key={transport.id}>
          <div className="wz-transport-summary__heading">
            <Typography.Text strong>{transport.transportCompany || t('transport.companyMissing')}</Typography.Text>
            {getTransportLabel(transport) && <Tag>{getTransportLabel(transport)}</Tag>}
          </div>
          <Row gutter={[24, 16]}>
            <Col xs={24} md={9}>
              <Typography.Text type="secondary">{t('transport.from')}</Typography.Text>
              <div>{joinLocation(transport.loadingAddresses, transport.originCity, transport.originCountry)}</div>
            </Col>
            <Col xs={24} md={9}>
              <Typography.Text type="secondary">{t('transport.to')}</Typography.Text>
              <div>{joinLocation(transport.deliveryAddresses, transport.destinationCity, transport.destinationCountry)}</div>
            </Col>
            <Col xs={24} md={6}>
              <Typography.Text type="secondary">{t('transport.rate')}</Typography.Text>
              <div>{formatRate(transport)}</div>
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );
};

export default WZTransportSummary;
